'use client';

import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card-layer';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import type { DebtData } from '@/types';
import { accountCurrency, debtNameByTransaction } from '@/lib/accountPayment';
import { fromMillicents } from '@/lib/currency';
import { groupByPayment, paymentGroupDebtLabel } from '../_paymentHistoryPartials/paymentGroups';
import { formatLedgerDate } from '../_shared/charges';
import { customerTransactions, transactionLowersBalance, transactionShowsPlusSign, transactionTitle } from '../_shared/TransactionsList';
import { Separator } from '@ophelos/ui/components/separator';

/** Enough to answer "did my payment land?" without turning the dashboard into a feed. */
const PREVIEW_COUNT = 3;

interface RecentPaymentsProps {
  debts: DebtData[];
}

export function RecentPayments({ debts }: RecentPaymentsProps) {
  const { t, currentLanguage } = useTranslation();

  const payments = groupByPayment(customerTransactions(debts)).slice(0, PREVIEW_COUNT);
  if (payments.length === 0) return null;

  const debtNames = debtNameByTransaction(debts);
  const currency = accountCurrency(debts);

  return (
    <>
      <Separator className="my-4" />
      <Card className="gap-2 overflow-visible mt-6" variant="fullscreen">
        <CardHeader className="h-full">
          <CardTitle className="tracking-wide">{t('dashboard.recentPayments')}</CardTitle>
        </CardHeader>

        <CardContent className="py-2 px-0 divide-y divide-border-light rounded-2xl">
          {payments.map((payment) => {
            const lead = payment.splits[0];
            const lowersBalance = transactionLowersBalance(lead);
            const showPlus = transactionShowsPlusSign(lead);
            const DirectionIcon = lowersBalance ? ArrowDownRight : ArrowUpRight;
            const [titleKey, titleValues] = transactionTitle(lead);

            return (
              <div key={payment.key} className="flex flex-col xs:flex-row xs:items-start justify-between gap-4 px-4 py-3">
                <div className="flex gap-3 flex-1 min-w-0">
                  <div className={`size-8 rounded-full flex items-center justify-center shrink-0 ${lowersBalance ? 'highlight-green' : 'bg-soft'}`}>
                    <DirectionIcon className="size-4" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <h5 className="font-medium text-sm text-left">{t(titleKey, titleValues)}</h5>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                      <span className="shrink-0">{formatLedgerDate(payment.createdAt, currentLanguage)}</span>
                      <span>
                        {t('transaction.appliedTo')} <span className="font-medium">{paymentGroupDebtLabel(payment, debtNames, t)}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={`inline-flex gap-2 items-center shrink-0 justify-center ${lowersBalance ? 'text-green-600' : showPlus ? 'text-amber-600' : ''}`}
                >
                  <span className="text-base">{lowersBalance ? '-' : showPlus ? '+' : ''}</span>
                  <CurrencyDisplay amount={fromMillicents(payment.amountMillicents)} currency={currency} className="font-semibold text-base" />
                </div>
              </div>
            );
          })}
        </CardContent>

        <Button asChild variant="linkMuted" size="sm" className="mx-auto">
          <Link to="/payment-history">
            {t('transaction.seeAllPayments')}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </Card>
    </>
  );
}
